'use no memo';

import {
  Action,
  Card,
  DataStatus,
  DeleteDialog,
  DropdownItem,
  DropdownItemProps,
  DropdownSubmenu,
  Upload,
  openDeleteDialog,
  openDialog,
  openDropdown,
  useDialog,
} from 'platform/components';
import {Box, Center, Heading, Hide, Icon, Show, Space, VStack} from 'platform/foundation';
import {Lightbox, LightboxAction, useLightbox} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';

import {memo, useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNonEmptyArray, isPositive} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleAlbumPhotoListQuery, useGetVehicleQuery} from '@dms/api/sales';
import {EntityResourceIds, VehicleAlbumResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, buildArray, parseDate, suffixTestId} from 'shared/web';

import {useDmsLightboxActionsFactory} from '../../hooks/useDmsLightboxActionsFactory';
import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {useVehicleAlbum} from '../../hooks/useVehicleAlbum';
import {useVehicleAlbumPhotoListInterval} from '../../hooks/useVehicleAlbumPhotoListInterval';
import {downloadFile} from '../../utils/downloadFile';
import {
  getAlbumUnknownMedia,
  getAlbumVideos,
  getFullSizeStorageUrl,
} from '../../utils/getGridPhotos';
import {getPrecedingPlayableVideos} from '../VideoPreview/utils/getPrecedingPlayableVideos';
import {isVideoPlayable} from '../VideoPreview/utils/isVideoPlayable';
import {VideoPreview} from '../VideoPreview/VideoPreview';
import {
  MAX_REORGANIZED_PHOTOS,
  useVehicleAlbumBulkActions,
} from './hooks/useVehicleAlbumBulkActions';
import {prepareVehicleAlbumImageUrl, VEHICLE_ALBUM_IMAGE_SIZE} from './utils';
import {VehicleAlbumDraggablePhoto} from './VehicleAlbumDraggablePhoto';
import {VehicleAlbumImage} from './VehicleAlbumImage';
import {VehicleAlbumImageGrid} from './VehicleAlbumImageGrid';
import {VehicleAlbumVideoUpload} from './VehicleAlbumVideoUpload';
import {VehicleRenameAlbumDialog} from './VehicleRenameAlbumDialog';

interface VehicleAlbumProps extends RequiredTestIdProps {
  title: string;
  createdAt: string;
  vehicleId: string;
  vehicleAlbumId: string;
  canUpload?: boolean;
  canDelete?: boolean;
  canRename?: boolean;
  hideWhenEmpty?: boolean;
  // Albums a photo may be moved/copied into (same service case). The gallery passes this
  // only in the single-service-case Photos tab; absent/empty means the submenus are hidden.
  moveCopyTargetAlbums?: Pick<VehicleAlbumResponseBody, 'id' | 'name' | 'translation'>[];
  onDownload?: () => void;
  isDownloadPending?: boolean;
  // Number of async copies into this album currently in flight (until each copy-finished push
  // lands), rendered as one "copying" placeholder tile each. Set by the gallery via onCopyStarted.
  receivingCopyCount?: number;
  onCopyStarted?: (destinationAlbumId: string, photoCount?: number) => void;
  canBulkSelect?: boolean;
  // Separate from moveCopyTargetAlbums: the vehicle tab drags without the right-click submenus.
  canDragPhotos?: boolean;
  onAlbumChange?: () => void;
}

const ALLOWED_MIME_TYPES_TO_UPLOAD = ['image/jpeg', 'image/png', 'image/jpg'].join(', ');
const VIDEO_TILE_RATIO = '4 / 3';

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
function VehicleAlbumComponent(props: VehicleAlbumProps) {
  const lightboxActionsFactory = useDmsLightboxActionsFactory();
  const formatDateTime = useDateTimeFormatter();

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const [canUploadVehicleAlbumPhotos, canDeleteVehicleAlbumPhoto, canMoveVehiclePhoto] =
    usePermissions({
      permissionKeys: ['createVehicleAlbumPhotos', 'deleteVehicleAlbumPhoto', 'moveVehiclePhoto'],
      scopes: {
        createVehicleAlbumPhotos: {
          participation: vehicleParticipation,
          branchId: vehicle?.branchId,
        },
        deleteVehicleAlbumPhoto: {
          participation: vehicleParticipation,
          branchId: vehicle?.branchId,
        },
        moveVehiclePhoto: {
          participation: vehicleParticipation,
          branchId: vehicle?.branchId,
        },
      },
    });

  const [
    isLightboxDeleteDialogOpen,
    openLightboxDeleteDialog,
    closeLightboxDeleteDialog,
    {storage: imageToDeleteId},
  ] = useDialog<string>();

  const vehicleAlbum = useVehicleAlbum({
    vehicleAlbumId: props.vehicleAlbumId,
    vehicleId: props.vehicleId,
  });
  const {pollingInterval} = useVehicleAlbumPhotoListInterval({
    vehicleId: props.vehicleId,
    vehicleAlbumId: props.vehicleAlbumId,
  });

  const {
    data: albumImages,
    isLoading: isLoadingAlbumImages,
    isError: hasAlbumImagesError,
  } = useGetVehicleAlbumPhotoListQuery(
    {
      vehicleId: props.vehicleId,
      vehicleAlbumId: props.vehicleAlbumId,
    },
    {
      skip: isNilOrEmpty(props.vehicleId) || isNilOrEmpty(props.vehicleAlbumId),
      pollingInterval,
    }
  );

  const [lightboxControls, {onOpen}] = useLightbox(props.vehicleAlbumId);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);

  const isUploadAllowed = canUploadVehicleAlbumPhotos && props.canUpload;

  const albumPhotos = (albumImages ?? []).filter((image) => image.mediaType === 'image');
  const albumVideos = getAlbumVideos(albumImages);
  const unknownMediaVideos = getAlbumUnknownMedia(albumImages);

  const bulkActions = useVehicleAlbumBulkActions({
    albumPhotoIds: albumPhotos.map((image) => image.id),
    actions: vehicleAlbum.actions,
    onAlbumChange: props.onAlbumChange,
    onCopyStarted: props.onCopyStarted,
  });

  if (
    props.hideWhenEmpty &&
    !isLoadingAlbumImages &&
    !isPositive(albumImages?.length || albumVideos?.length)
  ) {
    return null;
  }

  const handleSetAsCover = (photoId: string) =>
    vehicleAlbum.actions.setImageAsCoverPhoto(photoId).then((result) => {
      if (result) {
        props.onAlbumChange?.();
      }
    });

  const handleUnsetCover = () =>
    vehicleAlbum.actions.unsetImageAsCoverPhoto().then((result) => {
      if (result) {
        props.onAlbumChange?.();
      }
    });

  const handleDeleteImage = (photoId: string) =>
    vehicleAlbum.actions.deleteImageFromAlbum(photoId).then((result) => {
      if (result) {
        props.onAlbumChange?.();
      }
    });

  const handleMoveImage = (destinationAlbumId: string, photoId: string) =>
    vehicleAlbum.actions.moveImage(destinationAlbumId, photoId).then((result) => {
      if (result) {
        props.onAlbumChange?.();
      }
    });

  const handleCopyImage = (destinationAlbumId: string, photoId: string) =>
    vehicleAlbum.actions.copyImage(destinationAlbumId, photoId).then((result) => {
      if (result) {
        // Copy is accepted (204) but completes asynchronously — show a placeholder in the
        // destination album until the copy-finished push arrives and refreshes it.
        props.onCopyStarted?.(destinationAlbumId);
        props.onAlbumChange?.();
      }
    });

  // A photo can only be reorganized into a different album of the same set, so the
  // current album is excluded from its own target list.
  const moveCopyTargetAlbums = (props.moveCopyTargetAlbums ?? []).filter(
    (album) => album.id !== props.vehicleAlbumId
  );
  const canReorganizePhotos = canMoveVehiclePhoto && isNonEmptyArray(moveCopyTargetAlbums);
  const canBulkReorganize = props.canBulkSelect && canReorganizePhotos;
  // The drag must refuse the oversized move the "Move to" dropdown already refuses.
  const isDragBlockedByLimit = bulkActions.isBulkModeActive && bulkActions.isOverReorganizeLimit;

  // In bulk mode the gesture carries the whole Selection, so only its own tiles offer it.
  const isPhotoDraggable = (photoId: string) =>
    (props.canDragPhotos ?? false) &&
    !isDragBlockedByLimit &&
    (!bulkActions.isBulkModeActive || bulkActions.isImageSelected(photoId)) &&
    !vehicleAlbum.processingImageIds.includes(photoId);

  // Keyed on membership, not on mode: a tile outside the Selection moves only itself.
  const handleDragMove = (destinationAlbumId: string, photoId: string) =>
    bulkActions.isImageSelected(photoId)
      ? bulkActions.moveSelectedTo(destinationAlbumId)
      : handleMoveImage(destinationAlbumId, photoId);

  // Over the backend cap the dropdown collapses into a disabled button explaining why.
  const buildBulkReorganizeAction = (config: {
    title: string;
    testId: string;
    onSelect: (destinationAlbumId: string) => void;
  }): Action =>
    bulkActions.isOverReorganizeLimit
      ? {
          'data-testid': suffixTestId(config.testId, props),
          type: 'buttonWithTooltip',
          title: config.title,
          variant: 'ghostLink',
          leftIcon: 'file/folder',
          isDisabled: true,
          tooltip: {
            placement: 'top',
            label: i18n.t('entity.photo.labels.maxPhotosToReorganize', {
              count: MAX_REORGANIZED_PHOTOS,
            }),
          },
        }
      : {
          'data-testid': suffixTestId(config.testId, props),
          type: 'dropdown-button',
          title: config.title,
          variant: 'ghostLink',
          leftIcon: 'file/folder',
          menuItems: moveCopyTargetAlbums.map((album) => ({
            label: album.translation,
            onClick: () => config.onSelect(album.id),
          })),
        };

  const handleBulkDelete = () =>
    openDeleteDialog({
      text: i18n.t('entity.photo.actions.deleteSelectedPhotosConfirmation', {
        count: bulkActions.selectedCount,
      }),
      onConfirm: () => bulkActions.deleteSelected(),
    });

  const buildMoveCopySubmenus = (image: {id: string}) => {
    const isProcessing = vehicleAlbum.processingImageIds.includes(image.id);

    return (
      <>
        <DropdownSubmenu
          label={i18n.t('general.actions.copyTo')}
          prefix={<Icon value="file/folder" />}
          suffix={<Icon value="navigation/arrow_right" />}
          isDisabled={isProcessing}
        >
          {moveCopyTargetAlbums.map((album) => (
            <DropdownItem
              key={`copy_${album.id}`}
              label={album.translation}
              onClick={() => handleCopyImage(album.id, image.id)}
            />
          ))}
        </DropdownSubmenu>
        <DropdownSubmenu
          label={i18n.t('general.actions.moveTo')}
          prefix={<Icon value="file/folder" />}
          suffix={<Icon value="navigation/arrow_right" />}
          isDisabled={isProcessing}
        >
          {moveCopyTargetAlbums.map((album) => (
            <DropdownItem
              key={`move_${album.id}`}
              label={album.translation}
              onClick={() => handleMoveImage(album.id, image.id)}
            />
          ))}
        </DropdownSubmenu>
      </>
    );
  };

  const buildImageActionsContextMenu = (image: {id: string; isCoverPhoto: boolean}) => {
    const isProcessing = vehicleAlbum.processingImageIds.includes(image.id);

    // Narrowed to a string label so the rendered key needs no assertion.
    return buildArray<DropdownItemProps & {label: string}>([])
      .whenNot(image.isCoverPhoto, () => ({
        label: i18n.t('general.actions.setAsCoverPhoto'),
        prefix: <Icon value="image/photo" />,
        isDisabled: isProcessing,
        onClick: () => handleSetAsCover(image.id),
      }))
      .when(image.isCoverPhoto, () => ({
        label: i18n.t('general.actions.unsetCoverPhoto'),
        prefix: <Icon value="image/photo" />,
        isDisabled: isProcessing,
        onClick: () => handleUnsetCover(),
      }))
      .when(canDeleteVehicleAlbumPhoto, () => ({
        label: i18n.t('general.actions.delete'),
        prefix: <Icon value="action/delete" />,
        isDisabled: isProcessing,
        onClick: () => handleDeleteImage(image.id),
      }));
  };

  return (
    <Card
      data-testid={suffixTestId('vehicleAlbum', props)}
      title={props.title}
      subtitle={formatDateTime('dateShort', parseDate(props.createdAt))}
      actions={buildArray<Action>()
        .when(bulkActions.isBulkModeActive, {
          'data-testid': suffixTestId('vehicleAlbum-selectAll', props),
          type: 'button',
          title: i18n.t('general.actions.selectAll'),
          variant: 'ghostLink',
          leftIcon: 'action/done_all',
          onClick: bulkActions.selectAll,
        })
        .when(bulkActions.isBulkModeActive && bulkActions.hasSelection && canBulkReorganize, () =>
          buildBulkReorganizeAction({
            title: i18n.t('general.actions.copyTo'),
            testId: 'vehicleAlbum-bulkCopyTo',
            onSelect: bulkActions.copySelectedTo,
          })
        )
        .when(bulkActions.isBulkModeActive && bulkActions.hasSelection && canBulkReorganize, () =>
          buildBulkReorganizeAction({
            title: i18n.t('general.actions.moveTo'),
            testId: 'vehicleAlbum-bulkMoveTo',
            onSelect: bulkActions.moveSelectedTo,
          })
        )
        .when(
          bulkActions.isBulkModeActive && bulkActions.hasSelection && canDeleteVehicleAlbumPhoto,
          {
            'data-testid': suffixTestId('vehicleAlbum-bulkDelete', props),
            type: 'button',
            title: i18n.t('general.actions.delete'),
            variant: 'dangerGhost',
            leftIcon: 'action/delete',
            onClick: handleBulkDelete,
          }
        )
        .when(props.canRename && !bulkActions.isBulkModeActive, {
          'data-testid': suffixTestId('vehicleAlbum-renameAlbum', props),
          type: 'button',
          title: i18n.t('entity.vehicle.albums.rename'),
          variant: 'ghost',
          leftIcon: 'image/edit',
          onClick: () => {
            openDialog(
              <VehicleRenameAlbumDialog
                data-testid={suffixTestId('vehicleAlbum-renameAlbum', props)}
                vehicleId={props.vehicleId}
                serviceAlbumId={props.vehicleAlbumId}
                onComplete={props.onAlbumChange}
              />,
              {
                title: i18n.t('entity.vehicle.albums.rename'),
                size: 'small',
              }
            );
          },
        })
        .when(props.canDelete && !bulkActions.isBulkModeActive, {
          'data-testid': suffixTestId('vehicleAlbum-deleteAlbum', props),
          type: 'button',
          title: i18n.t('entity.vehicle.albums.delete'),
          variant: 'dangerGhost',
          leftIcon: 'action/delete',
          onClick: () => {
            openDeleteDialog({
              text: i18n.t('entity.vehicle.albums.deleteQuestion'),
              onConfirm: () =>
                vehicleAlbum.actions.deleteAlbum().then((result) => {
                  if (result) {
                    props.onAlbumChange?.();
                  }
                }),
            });
          },
        })
        .when(
          props.canBulkSelect && (isPositive(albumPhotos.length) || bulkActions.isBulkModeActive),
          {
            'data-testid': suffixTestId('vehicleAlbum-bulkActionsToggle', props),
            type: 'button',
            title: bulkActions.isBulkModeActive
              ? i18n.t('general.actions.cancel')
              : i18n.t('general.actions.bulkActions'),
            variant: 'ghostLink',
            leftIcon: 'image/filter_none',
            onClick: bulkActions.toggleBulkMode,
          }
        )
        .when(isNotNil(props.onDownload), {
          'data-testid': suffixTestId('vehicleAlbum-downloadAll', props),
          type: 'button',
          title: i18n.t('general.actions.downloadAll'),
          variant: 'link',
          leftIcon: 'file/download',
          isDisabled: isNilOrEmpty(albumImages) || props.isDownloadPending,
          onClick: () => props.onDownload?.(),
        })}
    >
      <Hide when={isPositive(albumImages?.length) || props.canUpload}>
        <Center width="100%" height={VEHICLE_ALBUM_IMAGE_SIZE.height}>
          <VStack align="center">
            <Icon value="image/image_alt" color="text.tertiary" size={8} />
            <Space vertical={2} />
            <Heading size={5} color="tertiary">
              {i18n.t('entity.vehicle.albums.empty')}
            </Heading>
          </VStack>
        </Center>
      </Hide>
      <Hide when={!isPositive(albumImages?.length) && !props.canUpload}>
        <VehicleAlbumImageGrid>
          <DataStatus isLoading={isLoadingAlbumImages} isError={hasAlbumImagesError}>
            {albumPhotos.map((image, index) => (
              <VehicleAlbumDraggablePhoto
                key={image.id}
                photoId={image.id}
                sourceAlbumId={props.vehicleAlbumId}
                previewUrl={prepareVehicleAlbumImageUrl(image.url)}
                photoCount={bulkActions.isBulkModeActive ? bulkActions.selectedCount : 1}
                isDisabled={!isPhotoDraggable(image.id)}
                onMove={handleDragMove}
              >
                <VehicleAlbumImage
                  src={prepareVehicleAlbumImageUrl(image.url)}
                  isCoverPhoto={image.isCoverPhoto}
                  isLoading={vehicleAlbum.processingImageIds.includes(image.id)}
                  isSelectable={bulkActions.isBulkModeActive}
                  isSelected={bulkActions.isImageSelected(image.id)}
                  data-testid={suffixTestId(`vehicleAlbum-images-[${index}]`, props)}
                  onClick={(event) =>
                    bulkActions.isBulkModeActive
                      ? bulkActions.handleTileClick(index, event.shiftKey)
                      : onOpen(index)
                  }
                  onContextMenu={(event) => {
                    if (bulkActions.isBulkModeActive) {
                      return;
                    }
                    openDropdown(
                      event,
                      <>
                        <Show when={canReorganizePhotos}>{buildMoveCopySubmenus(image)}</Show>
                        {buildImageActionsContextMenu(image).map((item) => (
                          <DropdownItem key={item.label} {...item} />
                        ))}
                      </>,
                      {id: 'vehicle-album-image-context-menu'}
                    );
                  }}
                />
              </VehicleAlbumDraggablePhoto>
            ))}
            {albumVideos.map((video, index) => (
              <VideoPreview
                key={video.id}
                videoAsset={video}
                ratio={VIDEO_TILE_RATIO}
                isLoading={!isVideoPlayable(video) || isNil(video.coverImageUrl)}
                isDisabled={vehicleAlbum.processingImageIds.includes(video.id)}
                onOpenPreview={() =>
                  onOpen(albumPhotos.length + getPrecedingPlayableVideos(albumVideos, index))
                }
                onDeleteVideo={
                  canDeleteVehicleAlbumPhoto
                    ? (videoAsset) => handleDeleteImage(videoAsset.id)
                    : undefined
                }
                data-testid={suffixTestId(`vehicleAlbum-videos-[${index}]`, props)}
              />
            ))}
            {unknownMediaVideos.map((video, index) => (
              <VideoPreview
                key={video.id}
                videoAsset={video}
                ratio={VIDEO_TILE_RATIO}
                isLoading
                data-testid={suffixTestId(`vehicleAlbum-unknownMedia-[${index}]`, props)}
              />
            ))}
            {vehicleAlbum.filePreview.list().map((preview, index) => (
              <VehicleAlbumImage
                data-testid={suffixTestId(`vehicleAlbum-imagePreviews-[${index}]`, props)}
                key={preview.id}
                src={preview.uri}
                isLoading
              />
            ))}
            {Array.from({length: props.receivingCopyCount ?? 0}).map((_, index) => (
              <VehicleAlbumImage
                key={`copyPlaceholder-${index}`} // eslint-disable-line react/no-array-index-key -- Pre-existing violation
                data-testid={suffixTestId(`vehicleAlbum-copyPlaceholder-[${index}]`, props)}
                src=""
                isLoading
              />
            ))}
            <Show when={isUploadAllowed}>
              <Box width="100%" height="100%" ratio="4 / 3">
                <Upload
                  isDisabled={isUploadInProgress}
                  data-testid={suffixTestId('vehicleAlbum-upload', props)}
                  type="card"
                  size="default"
                  accept={ALLOWED_MIME_TYPES_TO_UPLOAD}
                  isMultiple
                  shouldValidateAccept
                  uploadIcon="image/photo_camera"
                  onStart={(file) => {
                    setIsUploadInProgress(true);
                    vehicleAlbum.filePreview.show(file);
                  }}
                  onSuccess={() => {
                    setIsUploadInProgress(false);
                    props.onAlbumChange?.();
                  }}
                  onError={(...args) => {
                    setIsUploadInProgress(false);
                    vehicleAlbum.handlers.handleUploadError(...args);
                  }}
                  customRequest={({file, onSuccess, onError}) => {
                    if (file instanceof File) {
                      vehicleAlbum.actions.addImageToAlbum({
                        file,
                        onSuccess,
                        onError,
                      });
                    }
                  }}
                />
              </Box>
              <VehicleAlbumVideoUpload
                data-testid={suffixTestId('vehicleAlbum', props)}
                vehicleId={props.vehicleId}
                vehicleAlbumId={props.vehicleAlbumId}
                onUploaded={props.onAlbumChange}
              />
            </Show>
          </DataStatus>
        </VehicleAlbumImageGrid>
      </Hide>
      <Lightbox
        data-testid={suffixTestId('vehicleAlbum-lightbox', props)}
        controls={lightboxControls}
        images={lightboxActionsFactory.addFullActions(
          albumPhotos.map((image) => ({
            id: image.id,
            url: getFullSizeStorageUrl(image.url),
            resizeUrl: image.url,
          })),
          (image) =>
            buildArray<LightboxAction>().when(canDeleteVehicleAlbumPhoto, {
              id: 'deleteBtn',
              icon: 'action/delete',
              onClick: () => openLightboxDeleteDialog(image.id),
            })
        )}
        videos={lightboxActionsFactory.addShareOnlyActions(
          albumVideos.filter(isVideoPlayable),
          (video) =>
            buildArray<LightboxAction>([
              {
                id: 'downloadBtn',
                icon: 'file/download',
                onClick: () => {
                  if (isNotNil(video.originUrl)) {
                    downloadFile(video.originUrl);
                  }
                },
              },
            ]).when(canDeleteVehicleAlbumPhoto, {
              id: 'deleteBtn',
              icon: 'action/delete',
              onClick: () => openLightboxDeleteDialog(video.id),
            })
        )}
      />
      <DeleteDialog
        isOpen={isLightboxDeleteDialogOpen}
        onClose={closeLightboxDeleteDialog}
        onConfirm={() => {
          if (imageToDeleteId) {
            handleDeleteImage(imageToDeleteId);
          }
        }}
        isInLightbox
      />
      {lightboxActionsFactory.photoDownloadSizeDialog}
    </Card>
  );
}

export const VehicleAlbum = memo(VehicleAlbumComponent);
